import { useMemo } from 'react';
import { Button } from '@wayste/shadcn/components/button';
import { Popover, PopoverContent, PopoverTrigger } from '@wayste/shadcn/components/popover';
import { cn } from '@wayste/shadcn/lib/utils';
import { SourTableColumn } from '@wayste/shadcn/types/sour-table';
import { useSourSearchContext } from '@wayste/sour-search';
import { ArrowDown, ArrowUp, ListFilter } from 'lucide-react';

interface SourTableColumnHeaderProps<T> {
    column: SourTableColumn<T>;
    title: string;
    className?: string;
}

export function SourTableColumnHeader<T>({ column, title, className }: SourTableColumnHeaderProps<T>) {
    const { sourQueryStatus, handleUpdateSourQuery, onSubmit } = useSourSearchContext();
    const { sourQuery } = sourQueryStatus;

    const handleSort = (direction: 'ASC' | 'DESC' | undefined) => {
        const newQuery = handleUpdateSourQuery({ sort: direction ? { field: column.key as any, direction } : null });
        onSubmit(newQuery);
    };

    const handleSortCycle = () => {
        const currentSort = sourQuery.sort?.field === column.key ? sourQuery.sort?.direction : undefined;
        let nextDirection: 'ASC' | 'DESC' | undefined;

        if (!currentSort) {
            nextDirection = 'ASC';
        } else if (currentSort === 'ASC') {
            nextDirection = 'DESC';
        } else {
            nextDirection = undefined;
        }

        handleSort(nextDirection);
    };

    // Memoize expensive calculations
    const { filterButtonClass } = useMemo(() => {
        // Check if filtering is active for this column
        const checkFilterActive = (): boolean => {
            if (!sourQuery.filters || !column.key) return false;

            // Handle nested field paths (e.g., "customer.name")
            const keys = column.key.split('.');
            let currentFilter: Record<string, unknown> | undefined = sourQuery.filters;

            for (const key of keys) {
                if (!currentFilter || typeof currentFilter !== 'object') return false;
                const nextFilter = currentFilter[key];
                if (nextFilter && typeof nextFilter === 'object') {
                    currentFilter = nextFilter as Record<string, unknown>;
                } else {
                    return false;
                }
            }

            // A filter is active if it has an operator or a defined value
            return (
                currentFilter &&
                typeof currentFilter === 'object' &&
                (('operator' in currentFilter && currentFilter.operator !== undefined) ||
                    ('value' in currentFilter && currentFilter.value !== undefined))
            );
        };

        const filterActive = checkFilterActive();

        return {
            filterButtonClass: cn(
                'px-2 py-1 transition-colors hover:rounded-md hover:bg-surface-primary',
                filterActive && 'text-primary hover:text-primary',
            ),
        };
    }, [sourQuery.filters, column.key]);

    // If headerRender is provided, use it (without padding, let custom render control layout)
    if (column.headerRender) {
        return (
            <div
                className={cn(
                    column.headerAlign === 'center' && 'flex justify-center',
                    column.headerAlign === 'right' && 'flex justify-end',
                    className,
                )}
            >
                {column.headerRender()}
            </div>
        );
    }

    // If no interactive elements, just return the title
    if (!column.canSort && !column.canHide && !column.filter && !column.onFilterClick) {
        return (
            <div
                className={cn(column.headerAlign === 'center' && 'text-center', column.headerAlign === 'right' && 'text-right', className)}
            >
                {title}
            </div>
        );
    }

    return (
        <div
            className={cn(
                'flex items-center gap-1',
                column.headerAlign === 'center' && 'justify-center',
                column.headerAlign === 'right' && 'justify-end',
                className,
            )}
        >
            <div
                className={cn(
                    'flex items-center gap-1',
                    column.canSort && 'hover:bg-surface-primary cursor-pointer select-none transition-colors hover:rounded-md',
                )}
                onClick={column.canSort ? handleSortCycle : undefined}
            >
                <span className="flex-1">{title}</span>
                {/* Sort Button */}
                {column.canSort &&
                    sourQuery.sort?.field === column.key &&
                    (sourQuery.sort?.direction === 'ASC' ? (
                        <ArrowUp className="text-text-secondary size-4" />
                    ) : (
                        <ArrowDown className="text-text-secondary size-4" />
                    ))}
            </div>
            {/* Filter Button */}
            {column.filter && (
                <Popover>
                    <PopoverTrigger>
                        <Button variant="tertiary" size="sm" className={filterButtonClass} title="Filter">
                            <ListFilter className="size-4" />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto">{column.filter}</PopoverContent>
                </Popover>
            )}

            {column.onFilterClick && (
                <Button variant="tertiary" size="sm" className={filterButtonClass} title="Filter" onClick={column.onFilterClick}>
                    <ListFilter className="size-4" />
                </Button>
            )}
        </div>
    );
}
